import { useEffect, useState } from 'react';
import { useMarkets } from '@/state/markets';
import { getTicker } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { priceFmt, pct } from '@/lib/format';
import { cn } from '@/lib/cn';

interface Region {
  id: string;
  name: string;
  status: 'stable' | 'tense' | 'active' | 'sanctioned';
  exposure: string[];     // ticker labels (placeholders for Phase 4)
  blurb: string;
}

const REGIONS: Region[] = [
  { id: 'mea', name: 'Middle East', status: 'tense', exposure: ['$REGION.MEA', '$NARR.OIL-SHOCK'], blurb: 'Constant background risk premium. Trades pop on tweets.' },
  { id: 'eu', name: 'Europe', status: 'tense', exposure: ['$REGION.EU', '$WAR.EU-LITE'], blurb: 'Slow-burning everything. Bureaucracy is the only growth sector.' },
  { id: 'latam', name: 'Latin America', status: 'stable', exposure: ['$REGION.LATAM'], blurb: 'Currency drama on a 6-week cycle. Set a calendar reminder.' },
  { id: 'apac', name: 'Asia-Pacific', status: 'tense', exposure: ['$REGION.APAC', '$NARR.AI-WINTER'], blurb: 'Real economy. Fake numbers. Both bigger than yours.' },
  { id: 'us', name: 'United States', status: 'active', exposure: ['$NARR.RECESSION-FEAR', '$NARR.SOFT-LANDING'], blurb: 'You. The protagonist. Currently down 4% on the day.' },
  { id: 'arctic', name: 'Arctic Council', status: 'stable', exposure: ['$REGION.ARCTIC'], blurb: 'Quietly the most important geopolitical region. You will hear about it in 2031.' },
  { id: 'taiwan', name: 'Taiwan Strait', status: 'active', exposure: ['$CHIP', '$OAEN', '$NARR.SEMI-CRISIS'], blurb: 'Where most of the world\'s GPUs come from. The world is fine with this until it is suddenly not.' },
  { id: 'russia-russia', name: 'Russia v. Russia', status: 'active', exposure: ['$BOOM', '$NARR.HEDGE-OIL'], blurb: 'A border dispute between two regions of the same country. Neither side is winning. Both sides are losing different things.' },
  { id: 'iran', name: 'Iran', status: 'tense', exposure: ['$BOOM', '$NARR.OIL-SPIKE'], blurb: 'Nuclear program is "civilian". Diplomatic posture is "open".' },
  { id: 'antarctica', name: 'Antarctica', status: 'sanctioned', exposure: ['$NARR.PENGUIN-FUTURES'], blurb: 'Recently received a US tariff. Has not responded. Cannot respond. Penguins reportedly "fine."' },
  { id: 'mar-a-lago', name: 'Mar-a-Lago Sovereign Zone', status: 'active', exposure: ['$TARI', '$BOOM', '$WALL'], blurb: 'A residence that has been treated as a foreign embassy for 14 years. Functions as one too.' },
  { id: 'davos', name: 'Davos', status: 'stable', exposure: ['$NARR.GLOBALISM-LIVES'], blurb: 'A small Alpine town. Hosts 47 billionaires for 4 days a year. Vibe: ski lodge that decides things.' },
  { id: 'greenland', name: 'Greenland', status: 'tense', exposure: ['$WALL', '$NARR.ARCTIC-CLAIM'], blurb: 'Currently subject to a "renewed offer" from the President. Denmark is, allegedly, also offering. Greenland is, allegedly, busy.' },
  { id: 'silicon-valley', name: 'Silicon Valley', status: 'active', exposure: ['$CHIP', '$OAEN', '$AGI', '$SAAS'], blurb: 'A 50-square-mile area producing 47% of all "AI agent" announcements. Most are PowerPoint.' },
  { id: 'wall-street', name: 'Wall Street', status: 'active', exposure: ['$TARI', '$BOOM', '$GRFT'], blurb: 'Where you would, structurally, work, if you had not chosen to do this from a kitchen instead.' },
  { id: 'cayman', name: 'Cayman Islands', status: 'sanctioned', exposure: ['$NARR.OFFSHORE-FINE'], blurb: 'A small archipelago that, on paper, holds more capital than the entire EU. The paper is, technically, the asset.' },
  { id: 'mar-a-mar', name: 'Mar-a-Lago Annex (Saudi)', status: 'active', exposure: ['$BOOM', '$TARI', '$GRFT'], blurb: 'A second residence, built next to the first, with the same name, slightly different ownership. Embassy status: pending.' },
  { id: 'panama-canal', name: 'Panama Canal Sovereign Lease', status: 'tense', exposure: ['$WALL', '$BOOM'], blurb: 'Currently leased back to the US. Disputed. Lease terms: \$0/year for 99 years. Panama: surprised.' },
  { id: 'mars-claim', name: 'Mars Claim', status: 'sanctioned', exposure: ['$MUSK'], blurb: 'A unilateral claim of sovereignty over Mars. Recognized by 0 countries and 1 person. Stock pumped 14% on the announcement.' },
];

const STATUS_COLOR: Record<Region['status'], string> = {
  stable: 'var(--positive)',
  tense: 'var(--warning)',
  active: 'var(--critical)',
  sanctioned: 'var(--text-muted)',
};

export function WorldGlobe() {
  const [selected, setSelected] = useState<Region | null>(null);
  const setActive = useMarkets((s) => s.setActiveTicker);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5_000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--bg-deep)] overflow-hidden">
      <header className="flex shrink-0 items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] px-4 py-3">
        <span className="text-mono-md font-bold text-[var(--text-primary)] truncate">World Globe</span>
        <span className="text-mono-xs text-[var(--text-muted)] truncate ml-2 mr-10">exposure</span>
      </header>
      <div className="grid flex-1 min-h-0 grid-cols-1 grid-rows-[1fr_auto] gap-px bg-[var(--border-subtle)] sm:grid-cols-[1fr_240px] sm:grid-rows-1">
        <div className="min-h-0 overflow-y-auto bg-[var(--bg-deep)] p-3">
          <div className="grid grid-cols-2 gap-2">
            {REGIONS.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                className={cn(
                  'flex min-w-0 flex-col gap-1 rounded border p-2 text-left transition-colors overflow-hidden',
                  selected?.id === r.id
                    ? 'border-[var(--accent-primary)] bg-[var(--bg-elevated)]'
                    : 'border-[var(--border-subtle)] hover:bg-[var(--bg-elevated)]',
                )}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-block h-2 w-2 shrink-0 rounded-full" style={{ background: STATUS_COLOR[r.status] }} />
                  <span className="text-mono-xs font-bold text-[var(--text-primary)] truncate min-w-0 flex-1">{r.name}</span>
                </div>
                <span className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] truncate">
                  {r.status}
                </span>
              </button>
            ))}
          </div>
        </div>
        <aside className="min-h-0 overflow-y-auto bg-[var(--bg-surface)] p-3">
          {selected ? (
            <>
              <h3 className="text-mono-sm font-bold text-[var(--text-primary)] break-words">{selected.name}</h3>
              <div className="mt-1 text-[10px] uppercase tracking-wider" style={{ color: STATUS_COLOR[selected.status] }}>
                {selected.status}
              </div>
              <p className="mt-2 text-mono-xs text-[var(--text-secondary)] break-words">{selected.blurb}</p>
              <div className="mt-3 text-[10px] uppercase tracking-wider text-[var(--text-muted)]">exposure (tap to load chart)</div>
              <div className="mt-1 flex flex-col gap-1">
                {selected.exposure.map((t) => {
                  const id = t.replace(/^\$/, '').split('.')[0]!;
                  const cfg = getTicker(id);
                  if (!cfg) {
                    return (
                      <div key={t} className="flex items-center justify-between gap-2 rounded border border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-1 text-mono-xs">
                        <span className="text-[var(--text-primary)] truncate min-w-0 flex-1">{t}</span>
                        <span className="text-[var(--text-muted)] shrink-0">narrative</span>
                      </div>
                    );
                  }
                  const livePrice = priceAt(cfg, now);
                  const ref = priceAt(cfg, now - 24 * 60 * 60 * 1000);
                  const change = ref > 0 ? livePrice / ref - 1 : 0;
                  const positive = change >= 0;
                  return (
                    <button
                      key={t}
                      onClick={() => setActive(cfg.id)}
                      className="flex items-center justify-between gap-2 rounded border border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-1 text-mono-xs hover:bg-[var(--bg-elevated)] hover:border-[var(--accent-primary)] text-left"
                    >
                      <span className="text-[var(--text-primary)] truncate min-w-0 flex-1 font-bold">{cfg.id}</span>
                      <span className="text-[var(--text-secondary)] tabular-nums shrink-0">{priceFmt(livePrice)}</span>
                      <span className={cn('tabular-nums shrink-0', positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}>{pct(change)}</span>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="text-mono-xs text-[var(--text-muted)]">tap a region.</p>
          )}
        </aside>
      </div>
    </div>
  );
}
